import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
  Link,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { useMutation } from '@apollo/client';
import { PlanStep, IntakeStatus, StepStatus } from '../types/generated/graphql';
import { CREATE_STEP_INTAKE, SUBMIT_STEP_INTAKE_INLINE, GET_PLAN } from '../graphql/queries';
import SchemaFormRenderer from './intake/SchemaFormRenderer';
import { colors } from '../theme/colors';

type IntakePanelMode = 'collect' | 'outcome';

interface StepIntakePanelProps {
  planId: string;
  step: PlanStep;
  onIntakeComplete?: () => void;
  /** Mode determines the UI text and behavior:
   * - 'collect': For explicit data collection steps (CollectDocument, DataEntry intents)
   * - 'outcome': For human steps where we're capturing results/notes
   */
  mode?: IntakePanelMode;
}

/**
 * Panel for managing step intake - collecting data from users or capturing outcomes.
 * Supports:
 * - Inline form submission
 * - Generating shareable public links (collect mode only)
 * - Displaying completed intake results
 * 
 * @param mode - 'collect' for data collection, 'outcome' for human step outcome capture
 */
export const StepIntakePanel: React.FC<StepIntakePanelProps> = ({
  planId,
  step,
  onIntakeComplete,
  mode = 'collect',
}) => {
  const isOutcomeMode = mode === 'outcome';
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [rawText, setRawText] = useState('');
  // In outcome mode, show form expanded by default for easier data entry
  const [showForm, setShowForm] = useState(isOutcomeMode);
  const [showResults, setShowResults] = useState(true);
  const [copied, setCopied] = useState(false);

  const intake = step.intake;
  const isCompleted = intake?.status === IntakeStatus.Completed;
  const hasIntake = !!intake;
  const isLockedByConnector =
    step.status === StepStatus.WaitingExternal ||
    (step.status === StepStatus.Blocked &&
      (step.context as { connectorWait?: unknown })?.connectorWait != null);

  // Context-aware labels based on mode
  const labels = {
    headerTitle: isOutcomeMode ? 'Record Outcome' : 'Collect Information',
    headerIcon: isOutcomeMode ? AssignmentTurnedInIcon : DescriptionIcon,
    completedTitle: isOutcomeMode ? 'Outcome Recorded' : 'Data Collected',
    textFieldLabel: isOutcomeMode ? 'Outcome / Notes' : 'Additional Information',
    textFieldPlaceholder: isOutcomeMode 
      ? 'Record what you did, any information gathered, or notes about this step...'
      : 'Add any additional details, notes, or context...',
    submitButton: isOutcomeMode ? 'Save & Complete Step' : 'Submit & Complete Step',
    toggleButton: isOutcomeMode 
      ? (showForm ? 'Hide' : 'Add Notes')
      : (showForm ? 'Hide Form' : 'Fill Out Now'),
  };

  // Mutations
  const [createStepIntake, { loading: creatingIntake }] = useMutation(CREATE_STEP_INTAKE, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    awaitRefetchQueries: true,
  });

  const [submitIntakeInline, { loading: submitting, error: submitError }] = useMutation(
    SUBMIT_STEP_INTAKE_INLINE,
    {
      refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
      onCompleted: () => {
        onIntakeComplete?.();
      },
    }
  );

  // Create intake request for shareable link
  const handleCreateShareableLink = async () => {
    try {
      await createStepIntake({
        variables: {
          planId,
          stepId: step.id,
          prompt: step.description,
        },
      });
    } catch (err) {
      console.error('Failed to create intake request:', err);
    }
  };

  // Submit intake inline
  const handleSubmitInline = async () => {
    try {
      await submitIntakeInline({
        variables: {
          planId,
          stepId: step.id,
          input: {
            structuredData: Object.keys(formData).length > 0 ? formData : null,
            rawText: rawText || null,
            fileIds: [],
          },
        },
      });
      setFormData({});
      setRawText('');
      setShowForm(false);
    } catch (err) {
      console.error('Failed to submit intake:', err);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = () => {
    if (intake?.publicUrl) {
      navigator.clipboard.writeText(intake.publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Render completed intake results
  if (isCompleted) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: colors.green[50],
          border: `1px solid ${colors.green[100]}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CheckCircleIcon sx={{ color: colors.green[600] }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.green[600] }}>
            {labels.completedTitle}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowResults(!showResults)}
            sx={{
              ml: 'auto',
              transform: showResults ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={showResults}>
          {/* AI Summary */}
          {intake.summary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                AI Summary
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {intake.summary}
              </Typography>
            </Box>
          )}

          {/* Normalized Results */}
          {intake.normalizedResult && Object.keys(intake.normalizedResult as Record<string, unknown>).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                Collected Data
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: 0.5,
                  bgcolor: 'white',
                  borderRadius: 1,
                }}
              >
                {Object.entries(intake.normalizedResult as Record<string, unknown>).map(([key, value]) => (
                  <Box key={key} sx={{ display: 'flex', gap: 1, mb: 0.5, '&:last-child': { mb: 0 } }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: colors.textSecondary, minWidth: 120 }}
                    >
                      {key}:
                    </Typography>
                    <Typography variant="body2">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}

          {/* Raw Text */}
          {intake.rawText && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                Additional Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {intake.rawText}
              </Typography>
            </Box>
          )}

          {/* Submitted At */}
          {intake.submittedAt && (
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              Submitted {new Date(intake.submittedAt).toLocaleString()}
            </Typography>
          )}
        </Collapse>
      </Paper>
    );
  }

  // Render intake form or options
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: colors.gray[50],
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <labels.headerIcon sx={{ color: colors.blue[600] }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {labels.headerTitle}
        </Typography>
        {hasIntake && intake.status === IntakeStatus.Pending && (
          <Chip
            label="Pending"
            size="small"
            sx={{
              ml: 1,
              bgcolor: colors.amber[100],
              color: colors.amber[600],
              fontSize: '0.7rem',
            }}
          />
        )}
      </Box>

      {/* Shareable Link Section - only show in collect mode */}
      {!isOutcomeMode && hasIntake && intake.publicUrl && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
            Shareable Link
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.5,
              p: 1,
              bgcolor: 'white',
              borderRadius: 1,
              border: `1px solid ${colors.gray[200]}`,
            }}
          >
            <LinkIcon sx={{ color: colors.textSecondary, fontSize: 18 }} />
            <Link
              href={intake.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                flex: 1,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {intake.publicUrl}
            </Link>
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton size="small" onClick={handleCopyLink}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mt: 0.5 }}>
            Share this link to collect information from someone else
          </Typography>
        </Box>
      )}

      {/* Generate Shareable Link - only show in collect mode */}
      {!isOutcomeMode && !hasIntake && (
        <Button
          variant="outlined"
          size="small"
          startIcon={creatingIntake ? <CircularProgress size={14} /> : <LinkIcon />}
          onClick={handleCreateShareableLink}
          disabled={creatingIntake}
          sx={{ mb: 2 }}
        >
          Generate Shareable Link
        </Button>
      )}

      {!isOutcomeMode && <Divider sx={{ my: 2 }} />}

      {/* Inline Form Section */}
      <Box>
        {/* In outcome mode, show form expanded by default without toggle button */}
        {!isOutcomeMode && (
          <Button
            variant="text"
            size="small"
            onClick={() => setShowForm(!showForm)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showForm ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{ mb: 1 }}
          >
            {labels.toggleButton}
          </Button>
        )}

        <Collapse in={showForm}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Schema-based form */}
            {intake?.schema && (
              <SchemaFormRenderer
                schema={intake.schema}
                value={formData}
                onChange={setFormData}
              />
            )}

            {/* Free-form text */}
            <TextField
              label={labels.textFieldLabel}
              multiline
              rows={isOutcomeMode ? 4 : 3}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={labels.textFieldPlaceholder}
              fullWidth
              disabled={isLockedByConnector}
            />

            {submitError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {submitError.message}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleSubmitInline}
              disabled={isLockedByConnector || submitting || (!Object.keys(formData).length && !rawText)}
              startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              {labels.submitButton}
            </Button>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default StepIntakePanel;
